/* ═══════════════════════════════════════════════════════════════════════
   BROKEN HEARTS — Rebuild What Was Broken
   Self-contained vanilla Canvas 2D module. No dependencies, no backend.
   Coordinates: a fixed 1000×1000 "design space"; one transform maps it to
   the canvas (no letterbox — the visible field extends past [0,1000] on
   the long axis and scatter positions live there).
   Tuning: open with ?tune=1 — drag pieces, arrows nudge, E exports PIECES.
   ═══════════════════════════════════════════════════════════════════════ */
(() => {
'use strict';

/* ═══ 01 CONFIG ═══════════════════════════════════════════════════════ */

const ROOT = document.getElementById('bh-heart-game');
if (!ROOT) return;

const ASSET_BASE  = ROOT.dataset.assetBase || 'assets/';
const ENTER_URL   = ROOT.dataset.enterUrl || 'https://brokenhearts.co';
const BREAK_VIDEO = ROOT.dataset.breakVideo || '';
const TUNE        = new URLSearchParams(location.search).has('tune');

/* Fragmenta reward — wired by the Shopify section when the claim endpoint is
   configured. The theme signs `${customerId}:heart-game` with HMAC-SHA256;
   the Fragmenta app verifies the signature, credits once per customer, and
   answers 200 (credited) or 409 (already credited). Absent attrs = off. */
const CLAIM = {
  url: ROOT.dataset.claimUrl || '',
  customerId: ROOT.dataset.customerId || '',
  token: ROOT.dataset.claimToken || '',
  login: ROOT.dataset.claimLogin || '',
};

/* ── COORDINATE SYSTEM ──────────────────────────────────────────────────
   The completed heart was measured in a 256×256 reference image and is
   mapped into design space by:  design = REF_OFFSET + ref × REF_K.
   Change REF_K to resize the whole heart; per-piece values below are
   already baked into design units.
   Convention: targetX/targetY is where the CENTER OF THE 256×256 PNG
   CANVAS lands (not the gem's visual centroid).                        */
const REF_K      = 2.8;                  // ► ADJUST HERE: heart overall size
const REF_OFFSET = { x: 142, y: 140 };   // ► ADJUST HERE: heart position

/* ── PIECES ─────────────────────────────────────────────────────────────
   ► ADJUST HERE — the authoritative placement table.
   Use ?tune=1 → drag/arrow-key pieces → press E to export this block.
   startX/startY : scatter origin, landscape & desktop (may exceed 0–1000;
                   clamped into the visible field at runtime)
   portX/portY   : scatter origin, portrait phones
   targetRotation: 0 for ALL pieces (verified: reference bboxes reproduce
                   at rotation 0 on both axes)
   scale         : 0.514 measured fit × REF_K 2.8 = 1.439 design units/px
   snapRadius    : release distance (design units) that locks a piece
   bbox          : measured alpha bounds in the source 256px canvas —
                   used for clamping and fat-finger hit fallback only   */
const PIECES = [
  { id:'p1', file:'p1.png', startX:  70, startY:270, portX:310, portY: 80,
    targetX:498.7, targetY:396.8, rotation:0, targetRotation:0, scale:1.439, snapRadius:55,
    bbox:{x:66,y:41,w:126,h:174} },   // center cleft — tall light shard between the lobes
  { id:'p2', file:'p2.png', startX:  40, startY:620, portX:160, portY:930,
    targetX:316.4, targetY:570.1, rotation:0, targetRotation:0, scale:1.439, snapRadius:55,
    bbox:{x:34,y:58,w:196,h:159} },   // lower-left flank — dark wedge pointing to the tip
  { id:'p3', file:'p3.png', startX: 945, startY:700, portX:500, portY:950,
    targetX:526.7, targetY:654.4, rotation:0, targetRotation:0, scale:1.439, snapRadius:55,
    bbox:{x:28,y:42,w:206,h:188} },   // bottom point — largest piece, tapers to the tip
  { id:'p4', file:'p4.png', startX: 930, startY:520, portX:840, portY:920,
    targetX:680.7, targetY:525.8, rotation:0, targetRotation:0, scale:1.439, snapRadius:55,
    bbox:{x:22,y:58,w:204,h:154} },   // mid-right flank — darkest anthracite shield
  { id:'p5', file:'p5.png', startX:  90, startY:430, portX:120, portY:130,
    targetX:367.4, targetY:298.5, rotation:0, targetRotation:0, scale:1.439, snapRadius:55,
    bbox:{x:47,y:74,w:157,h:112} },   // top-left lobe cap
  { id:'p6', file:'p6.png', startX:  55, startY:770, portX:330, portY:905,
    targetX:280.3, targetY:391.2, rotation:0, targetRotation:0, scale:1.439, snapRadius:55,
    bbox:{x:45,y:31,w:198,h:176} },   // left lobe outer edge — large fan
  { id:'p7', file:'p7.png', startX: 950, startY:330, portX:690, portY:110,
    targetX:761.4, targetY:360.1, rotation:0, targetRotation:0, scale:1.439, snapRadius:55,
    bbox:{x:34,y:47,w:145,h:147} },   // far-right point — near-white trillion (NOT swappable with p8)
  { id:'p8', file:'p8.png', startX: 915, startY:180, portX:870, portY: 95,
    targetX:649.4, targetY:298.8, rotation:0, targetRotation:0, scale:1.439, snapRadius:55,
    bbox:{x:48,y:70,w:141,h:119} },   // top-right lobe cap — brightest piece; its art sits
                                      // off-center in its canvas, so p8 is the litmus test for
                                      // placement: if p8 looks shifted, open ?tune=1
];
const PIECE_SEED = PIECES.map(p => ({ ...p, bbox: { ...p.bbox } }));

/* approximate seam polylines (ref 256 coords) for the scar gleam and the
   seam-kiss; pair = the two pieces that share the crack —
   ► ADJUST HERE if a gleam visibly leaves its crack */
const SEAMS_REF = [
  { pts: [[118, 45], [112, 132]], pair: ['p1', 'p6'] },   // cleft, left edge
  { pts: [[118, 45], [112, 80]],  pair: ['p1', 'p5'] },   // cleft, upper-left edge
  { pts: [[140, 45], [147, 122]], pair: ['p1', 'p8'] },   // cleft, right edge
  { pts: [[45, 82],  [110, 78]],  pair: ['p5', 'p6'] },   // left lobe cap line
  { pts: [[26, 122], [114, 136]], pair: ['p6', 'p2'] },   // left mid seam
  { pts: [[92, 178], [128, 222]], pair: ['p2', 'p3'] },   // lower-left diagonal
  { pts: [[150, 116],[192, 109]], pair: ['p8', 'p4'] },   // right upper seam, inner half
  { pts: [[192, 109],[234, 102]], pair: ['p7', 'p4'] },   // right upper seam, outer half
  { pts: [[156, 152],[186, 206]], pair: ['p4', 'p3'] },   // right lower seam
];

const MAGNET_R = 120;                 // design units — magnetic field radius
const FIELD_MARGIN = 40;              // clamp margin inside the visible field
const HUD_BAND = 120;                 // design units kept clear under the HUD

const ROMAN = ['I','II','III','IV','V','VI','VII','VIII'];

/* duration ladder — golden-ratio steps around the 618ms brand unit */
const T_MICRO = 146, T_UI = 236, T_PULL = 382, T_STD = 618, T_SCENE = 1000, T_CINE = 1618;

/* ═══ 02 UTILS ════════════════════════════════════════════════════════ */

const DEG = Math.PI / 180;
const clamp = (v, a, b) => v < a ? a : v > b ? b : v;
const clamp01 = v => v < 0 ? 0 : v > 1 ? 1 : v;
const lerp = (a, b, t) => a + (b - a) * t;
const dist = (x1, y1, x2, y2) => Math.hypot(x2 - x1, y2 - y1);
const rand = (a, b) => a + Math.random() * (b - a);

const easeOutCubic = t => 1 - Math.pow(1 - t, 3);
const easeOutQuint = t => 1 - Math.pow(1 - t, 5);
const easeInOutSine = t => -(Math.cos(Math.PI * t) - 1) / 2;
const easeRitual = t => 1 - Math.pow(1 - t, 4);            // ~cubic-bezier(.22,1,.36,1)
const easeOutBack = t => { const s = 1.4; const u = t - 1; return 1 + (s + 1) * u * u * u + s * u * u; };

const store = {
  get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch (e) {} },
};

const RM = matchMedia('(prefers-reduced-motion: reduce)');
const reduced = () => RM.matches;
const COARSE = matchMedia('(pointer: coarse)').matches;

/* accepts a number or a vibration pattern array; silent under reduced motion */
const buzz = p => { try { if (navigator.vibrate && !RM.matches) navigator.vibrate(p); } catch (e) {} };

/* minimal tween engine */
const tweens = [];
function tween(dur, ease, step, done) {
  tweens.push({ t0: performance.now(), dur, ease, step, done });
}
function updateTweens(now) {
  for (let i = tweens.length - 1; i >= 0; i--) {
    const o = tweens[i];
    const k = clamp01((now - o.t0) / o.dur);
    o.step(o.ease ? o.ease(k) : k);
    if (k >= 1) { tweens.splice(i, 1); if (o.done) o.done(); }
  }
}
function clearTweens() { tweens.length = 0; }

/* state-scoped cue list */
let cues = [];
const setCues = list => { cues = list.map(c => ({ at: c[0], fn: c[1], fired: false })); };
const runCues = elapsed => { for (const c of cues) if (!c.fired && elapsed >= c.at) { c.fired = true; c.fn(); } };

function fmtTime(ms) {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/* ═══ 03 VIEWPORT ═════════════════════════════════════════════════════ */

const stage  = ROOT.querySelector('.bhg-stage');
const canvas = ROOT.querySelector('.bhg-canvas');
const ctx    = canvas.getContext('2d');

const VP = { w: 0, h: 0, dpr: 1, k: 1, tx: 0, ty: 0, visL: 0, visR: 1000, visT: 0, visB: 1000, portrait: false };

function measureViewport() {
  VP.w = stage.clientWidth;
  VP.h = stage.clientHeight;
  VP.dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width  = Math.round(VP.w * VP.dpr);
  canvas.height = Math.round(VP.h * VP.dpr);
  VP.k  = Math.min(VP.w, VP.h) / 1000;
  VP.tx = VP.w / 2 - 500 * VP.k;
  VP.ty = VP.h / 2 - 500 * VP.k;
  VP.visL = 500 - VP.w / (2 * VP.k);
  VP.visR = 500 + VP.w / (2 * VP.k);
  VP.visT = 500 - VP.h / (2 * VP.k);
  VP.visB = 500 + VP.h / (2 * VP.k);
  VP.portrait = VP.h > VP.w * 1.05;
}
const designTransform = c => c.setTransform(VP.k * VP.dpr, 0, 0, VP.k * VP.dpr, VP.tx * VP.dpr, VP.ty * VP.dpr);
const identity = c => c.setTransform(1, 0, 0, 1, 0, 0);

/* heart rect in design units (the 256 reference mapped through REF_K) */
const heartRect = () => ({ x: REF_OFFSET.x, y: REF_OFFSET.y, w: 256 * REF_K, h: 256 * REF_K });
const HEART_CENTER = { x: 497.5, y: 468 };

function clampToField(p, x, y) {
  const s = p.scale;
  const halfW = p.bbox.w / 2 * s, halfH = p.bbox.h / 2 * s;
  const offX = (p.bbox.x + p.bbox.w / 2 - 128) * s;
  const offY = (p.bbox.y + p.bbox.h / 2 - 128) * s;
  // never let a resting piece sit inside the iOS edge-swipe zone (~26 CSS px)
  const m = Math.max(FIELD_MARGIN, 26 / VP.k);
  return {
    x: clamp(x, VP.visL + m + halfW - offX, VP.visR - m - halfW - offX),
    y: clamp(y, VP.visT + HUD_BAND + halfH - offY, VP.visB - m - halfH - offY),
  };
}

/* ═══ 04 ASSETS ═══════════════════════════════════════════════════════ */

const looksLikeCdn = /\/\//.test(ASSET_BASE);
const assetUrl = file => looksLikeCdn ? `${ASSET_BASE}bh-heart-${file}` : `${ASSET_BASE}${file}`;

let refImg = null;
const caches = {
  bg: null, grain: [], grainPat: [], ghost: null, heartMask: null, hullMask: null,
  scar: null, scarRest: null, scarSpot: null, sweep: null, sweepBand: null,
  flash: null, dots: [], dim: null,
};

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';        // Shopify CDN sends CORS headers; keeps getImageData untainted
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load ${file}`));
    img.src = assetUrl(file);
  });
}

async function loadAssets() {
  const imgs = await Promise.all([...PIECES.map(p => loadImage(p.file)), loadImage('ref.png')]);
  PIECES.forEach((p, i) => { p.img = imgs[i]; });
  refImg = imgs[8];
  buildHitMasks();
}

function buildHitMasks() {
  for (const p of PIECES) {
    const c = document.createElement('canvas');
    c.width = 64; c.height = 64;
    const x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(p.img, 0, 0, 64, 64);
    p.mask = x.getImageData(0, 0, 64, 64).data;   // RGBA — sample alpha at [i*4+3]
  }
}

function makeCanvas(w, h) {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  return c;
}

/* pieces pre-rendered at device resolution with shadows BAKED — the rAF
   loop never touches shadowBlur/filter. Cache is square and centered on
   the source canvas center, padded for the shadow spill. */
function buildPieceCache(p, lifted) {
  const px = VP.k * VP.dpr;                       // device px per design unit
  const drawW = 256 * p.scale;                    // design units
  const pad = 76;                                 // design units of shadow headroom
  const size = drawW + pad * 2;
  const c = makeCanvas(size * px, size * px);
  const x = c.getContext('2d');
  x.imageSmoothingEnabled = true;
  x.imageSmoothingQuality = 'high';
  // pass 1: grounding dark shadow
  x.save();
  x.shadowColor = lifted ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.55)';
  x.shadowBlur = (lifted ? 34 : 22) * px;
  x.shadowOffsetY = (lifted ? 20 : 13) * px;
  x.drawImage(p.img, pad * px, pad * px, drawW * px, drawW * px);
  x.restore();
  // pass 2: cool silver sheen (never neon: low alpha, wide blur)
  x.save();
  x.shadowColor = lifted ? 'rgba(224,228,234,0.28)' : 'rgba(218,222,228,0.16)';
  x.shadowBlur = (lifted ? 15 : 10) * px;
  x.drawImage(p.img, pad * px, pad * px, drawW * px, drawW * px);
  x.restore();
  return { canvas: c, size };                     // size in design units
}

/* end-state dim: darkness pools at the frame edges, the jewel keeps its
   spotlight. Baked alphas multiply by dimAlpha at draw time, so the final
   dimAlpha 0.35 gives ~0.14 over the heart and 0.35 at the edges. */
function buildDim() {
  const c = makeCanvas(VP.w * VP.dpr, VP.h * VP.dpr);
  const x = c.getContext('2d');
  const cx = (HEART_CENTER.x * VP.k + VP.tx) * VP.dpr;
  const cy = (HEART_CENTER.y * VP.k + VP.ty) * VP.dpr;
  const g = x.createRadialGradient(cx, cy, 0, cx, cy, 640 * VP.k * VP.dpr);
  g.addColorStop(0, 'rgba(3,3,4,0.40)');
  g.addColorStop(0.45, 'rgba(3,3,4,0.62)');
  g.addColorStop(1, 'rgba(3,3,4,1)');
  x.fillStyle = g;
  x.fillRect(0, 0, c.width, c.height);
  caches.dim = c;
}

/* the dragged piece's own silhouette, ghost-treated — drawn at its target
   while inside the magnet field: the heart shows the gem its place */
function buildGhostSlot(p) {
  const px = VP.k * VP.dpr / 2;                   // half res — it is a blur
  const w = Math.max(2, 256 * p.scale * px);
  const lift = makeCanvas(w, w);
  let x = lift.getContext('2d');
  x.drawImage(p.img, 0, 0, w, w);
  x.globalCompositeOperation = 'source-atop';
  x.fillStyle = 'rgba(223,226,232,0.62)';
  x.fillRect(0, 0, w, w);
  const small = makeCanvas(Math.max(1, w / 5), Math.max(1, w / 5));
  small.getContext('2d').drawImage(lift, 0, 0, small.width, small.height);
  const out = makeCanvas(w, w);
  x = out.getContext('2d');
  x.imageSmoothingEnabled = true;
  x.imageSmoothingQuality = 'high';
  x.drawImage(small, 0, 0, w, w);
  x.globalAlpha = 0.85;
  x.drawImage(lift, 0, 0, w, w);
  return { canvas: out, size: 256 * p.scale };
}

function buildBg() {
  const c = makeCanvas(VP.w * VP.dpr, VP.h * VP.dpr);
  const x = c.getContext('2d');
  const w = c.width, h = c.height;
  x.fillStyle = '#050505';
  x.fillRect(0, 0, w, h);
  const r = Math.max(w, h) * 1.2;
  const g = x.createRadialGradient(w / 2, h * 0.4, 0, w / 2, h * 0.4, r);
  g.addColorStop(0, '#101013');
  g.addColorStop(0.45, '#08080A');
  g.addColorStop(1, '#030304');
  x.fillStyle = g;
  x.fillRect(0, 0, w, h);
  const v = x.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.45, w / 2, h / 2, Math.hypot(w, h) / 2);
  v.addColorStop(0, 'rgba(0,0,0,0)');
  v.addColorStop(1, 'rgba(0,0,0,0.5)');
  x.fillStyle = v;
  x.fillRect(0, 0, w, h);
  caches.bg = c;
}

function buildGrain() {
  if (caches.grain.length) return;
  for (let t = 0; t < 3; t++) {
    const c = makeCanvas(160, 160);
    const x = c.getContext('2d');
    const d = x.createImageData(160, 160);
    for (let i = 0; i < d.data.length; i += 4) {
      const v = (Math.random() * 255) | 0;
      d.data[i] = d.data[i + 1] = d.data[i + 2] = v;
      d.data[i + 3] = 255;
    }
    x.putImageData(d, 0, 0);
    caches.grain.push(c);
    caches.grainPat.push(ctx.createPattern(c, 'repeat'));
  }
}

function buildGhostAndMasks() {
  const hr = heartRect();
  const px = VP.k * VP.dpr;
  const w = hr.w * px, h = hr.h * px;

  // lifted silver spectre: ref + flat silver wash, then a soft halo copy under a crisp copy
  const lift = makeCanvas(w, h);
  let x = lift.getContext('2d');
  x.drawImage(refImg, 0, 0, w, h);
  x.globalCompositeOperation = 'source-atop';
  x.fillStyle = 'rgba(223,226,232,0.62)';
  x.fillRect(0, 0, w, h);

  const small = makeCanvas(w / 5, h / 5);          // downscale-upscale = cheap pre-blur
  small.getContext('2d').drawImage(lift, 0, 0, small.width, small.height);

  const ghost = makeCanvas(w, h);
  x = ghost.getContext('2d');
  x.imageSmoothingEnabled = true;
  x.imageSmoothingQuality = 'high';
  x.globalAlpha = 0.9;
  x.drawImage(small, 0, 0, w, h);
  x.globalAlpha = 1;
  x.drawImage(lift, 0, 0, w, h);
  caches.ghost = ghost;

  // opaque silhouette WITH crack holes — clips the completion light sweep
  const mask = makeCanvas(w, h);
  x = mask.getContext('2d');
  x.drawImage(refImg, 0, 0, w, h);
  x.globalCompositeOperation = 'source-in';
  x.fillStyle = '#fff';
  x.fillRect(0, 0, w, h);
  caches.heartMask = mask;

  // dilated hull (cracks closed) — bounds the scar-light layer behind the gems
  const hull = makeCanvas(w, h);
  x = hull.getContext('2d');
  const r = 4 * px;
  for (let a = 0; a < 8; a++) x.drawImage(mask, Math.cos(a * Math.PI / 4) * r, Math.sin(a * Math.PI / 4) * r);
  x.drawImage(mask, 0, 0);
  x.globalCompositeOperation = 'source-in';
  x.fillStyle = '#fff';
  x.fillRect(0, 0, w, h);
  caches.hullMask = hull;

  caches.scar = makeCanvas(w, h);
  caches.sweep = makeCanvas(w, h);

  // resting scar light: one large soft pool behind the whole heart
  const rest = makeCanvas(w, h);
  x = rest.getContext('2d');
  const g = x.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) / 2);
  g.addColorStop(0, 'rgba(235,238,242,0.9)');
  g.addColorStop(1, 'rgba(235,238,242,0)');
  x.fillStyle = g;
  x.fillRect(0, 0, w, h);
  caches.scarRest = rest;

  // travelling gleam hotspot (~80 design units)
  const spotR = 80 * px;
  const spot = makeCanvas(spotR * 2, spotR * 2);
  x = spot.getContext('2d');
  const sg = x.createRadialGradient(spotR, spotR, 0, spotR, spotR, spotR);
  sg.addColorStop(0, 'rgba(244,246,249,0.95)');
  sg.addColorStop(1, 'rgba(244,246,249,0)');
  x.fillStyle = sg;
  x.fillRect(0, 0, spotR * 2, spotR * 2);
  caches.scarSpot = spot;

  // completion sweep band: narrow specular gradient, tilted
  const band = makeCanvas(w * 1.2, h * 1.2);
  x = band.getContext('2d');
  const ang = 115 * DEG;
  const dx = Math.cos(ang) * band.width, dy = Math.sin(ang) * band.width;
  const bg2 = x.createLinearGradient(band.width / 2 - dx / 2, band.height / 2 - dy / 2,
                                     band.width / 2 + dx / 2, band.height / 2 + dy / 2);
  bg2.addColorStop(0.44, 'rgba(244,246,249,0)');
  bg2.addColorStop(0.50, 'rgba(244,246,249,0.5)');
  bg2.addColorStop(0.56, 'rgba(244,246,249,0)');
  x.fillStyle = bg2;
  x.fillRect(0, 0, band.width, band.height);
  caches.sweepBand = band;
}

function buildFxSprites() {
  if (caches.flash) return;
  const flash = makeCanvas(256, 256);
  let x = flash.getContext('2d');
  const fg = x.createRadialGradient(128, 128, 0, 128, 128, 128);
  fg.addColorStop(0, 'rgba(240,242,246,0.9)');
  fg.addColorStop(0.4, 'rgba(226,229,235,0.35)');
  fg.addColorStop(1, 'rgba(226,229,235,0)');
  x.fillStyle = fg;
  x.fillRect(0, 0, 256, 256);
  caches.flash = flash;

  for (const r of [8, 18]) {                       // soft silver motes, two sizes
    const c = makeCanvas(r * 2, r * 2);
    x = c.getContext('2d');
    const g = x.createRadialGradient(r, r, 0, r, r, r);
    g.addColorStop(0, 'rgba(242,243,245,1)');
    g.addColorStop(0.5, 'rgba(220,224,230,0.5)');
    g.addColorStop(1, 'rgba(220,224,230,0)');
    x.fillStyle = g;
    x.fillRect(0, 0, r * 2, r * 2);
    caches.dots.push(c);
  }
  const star = makeCanvas(18, 18);                 // rare four-point glint, echoes the painted ones
  x = star.getContext('2d');
  x.strokeStyle = 'rgba(242,243,245,0.95)';
  x.lineWidth = 1.4;
  x.beginPath();
  x.moveTo(9, 0); x.lineTo(9, 18);
  x.moveTo(0, 9); x.lineTo(18, 9);
  x.stroke();
  caches.dots.push(star);
}

let placedCanvas = null, placedCtx = null;
function buildPlacedCanvas() {
  placedCanvas = makeCanvas(VP.w * VP.dpr, VP.h * VP.dpr);
  placedCtx = placedCanvas.getContext('2d');
}
function stampPiece(p) {
  designTransform(placedCtx);
  placedCtx.save();
  placedCtx.translate(p.targetX, p.targetY);
  placedCtx.rotate(p.targetRotation * DEG);
  const sz = p.cache.size;
  placedCtx.drawImage(p.cache.canvas, -sz / 2, -sz / 2, sz, sz);
  placedCtx.restore();
}

function rebuildCaches() {
  buildBg();
  buildDim();
  buildGrain();
  buildFxSprites();
  if (refImg) {
    buildGhostAndMasks();
    for (const p of PIECES) {
      p.cache = buildPieceCache(p, false);
      p.cacheLift = buildPieceCache(p, true);
      p.ghostSlot = buildGhostSlot(p);
    }
    buildPlacedCanvas();
    for (const p of PIECES) if (p.placed) stampPiece(p);
  }
}

/* ═══ 05 AUDIO ════════════════════════════════════════════════════════ */

let ac = null, master = null, space = null, noiseBuf = null;
let muted = store.get('bh_heart_muted') === '1';

function initAudio() {
  if (ac) return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  try {
    ac = new AC();
    // iOS: route as 'playback' so the hardware silent switch does not kill the game audio
    try { if ('audioSession' in navigator) navigator.audioSession.type = 'playback'; } catch (e) {}
    master = ac.createGain();
    master.gain.value = muted ? 0 : 1;
    master.connect(ac.destination);
    // a small "space": feedback delay through a lowpass
    space = ac.createGain();
    const delay = ac.createDelay(0.5); delay.delayTime.value = 0.18;
    const fb = ac.createGain(); fb.gain.value = 0.25;
    const lp = ac.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 3000;
    space.connect(master);
    space.connect(delay);
    delay.connect(lp); lp.connect(fb); fb.connect(delay);
    lp.connect(master);
    noiseBuf = ac.createBuffer(1, 4410, ac.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    // unlock with a silent blip
    const o = ac.createOscillator(); const g = ac.createGain();
    g.gain.value = 0.0001; o.connect(g); g.connect(master);
    o.start(); o.stop(ac.currentTime + 0.02);
  } catch (e) { ac = null; }
}

/* crystalline chime — eight ascending quarter-tones; the 8th lands a
   perfect fourth above the 1st */
function chime(n) {
  if (!ac) return;
  if (ac.state !== 'running') ac.resume();
  const t0 = ac.currentTime + 0.02;
  const f0 = 1318.51 * Math.pow(2, n / 24);
  for (const [ratio, gain] of [[1, 1], [2.01, 0.4], [2.99, 0.2]]) {
    const o = ac.createOscillator();
    o.type = 'sine';
    o.frequency.value = f0 * ratio;
    o.detune.value = rand(-4, 4);
    const g = ac.createGain();
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(gain * 0.2, t0 + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0002, t0 + 0.9);
    o.connect(g); g.connect(space);
    o.start(t0); o.stop(t0 + 1);
  }
  const nb = ac.createBufferSource(); nb.buffer = noiseBuf;
  const bp = ac.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 5000; bp.Q.value = 1;
  const ng = ac.createGain();
  ng.gain.setValueAtTime(0.13, t0);
  ng.gain.exponentialRampToValueAtTime(0.0005, t0 + 0.06);
  nb.connect(bp); bp.connect(ng); ng.connect(space);
  nb.start(t0); nb.stop(t0 + 0.08);
}

/* completion pad — D3 + A3 + D4, slow swell */
function pad() {
  if (!ac) return;
  if (ac.state !== 'running') ac.resume();
  const t0 = ac.currentTime + 0.05;
  const lp = ac.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 800;
  const g = ac.createGain();
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(0.12, t0 + 0.4);
  g.gain.setValueAtTime(0.12, t0 + 2.2);
  g.gain.linearRampToValueAtTime(0.0001, t0 + 4.7);
  lp.connect(g); g.connect(master);
  for (const f of [146.83, 220.0, 293.66]) {
    for (const type of ['sine', 'triangle']) {
      const o = ac.createOscillator();
      o.type = type;
      o.frequency.value = f;
      o.detune.value = rand(-5, 5);
      const og = ac.createGain(); og.gain.value = type === 'sine' ? 0.5 : 0.18;
      o.connect(og); og.connect(lp);
      o.start(t0); o.stop(t0 + 5);
    }
  }
}

/* low thud — the dagger lands (prologue); reused darker and quieter under
   the eighth snap */
function thud(peak, f0, f1) {
  if (!ac) return;
  if (ac.state !== 'running') ac.resume();
  peak = peak || 0.2; f0 = f0 || 58; f1 = f1 || 36;
  const t0 = ac.currentTime + 0.01;
  const o = ac.createOscillator();
  o.type = 'sine';
  o.frequency.setValueAtTime(f0, t0);
  o.frequency.exponentialRampToValueAtTime(f1, t0 + 0.28);
  const g = ac.createGain();
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(peak, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0002, t0 + 0.5);
  o.connect(g); g.connect(master);
  o.start(t0); o.stop(t0 + 0.6);
  const nb = ac.createBufferSource(); nb.buffer = noiseBuf;
  const lp = ac.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 240;
  const ng = ac.createGain();
  ng.gain.setValueAtTime(peak * 0.8, t0);
  ng.gain.exponentialRampToValueAtTime(0.0005, t0 + 0.18);
  nb.connect(lp); lp.connect(ng); ng.connect(master);
  nb.start(t0); nb.stop(t0 + 0.2);
}

/* pickup foley — a faint glass touch, octave below the chime root, wet tail */
function pick() {
  if (!ac) return;
  const t0 = ac.currentTime + 0.01;
  const o = ac.createOscillator();
  o.type = 'sine';
  o.frequency.value = 659.26;
  o.detune.value = rand(-3, 3);
  const g = ac.createGain();
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(0.04, t0 + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0002, t0 + 0.18);
  o.connect(g); g.connect(space);
  o.start(t0); o.stop(t0 + 0.25);
  const nb = ac.createBufferSource(); nb.buffer = noiseBuf;
  const bp = ac.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 4500; bp.Q.value = 1.2;
  const ng = ac.createGain();
  ng.gain.setValueAtTime(0.06, t0);
  ng.gain.exponentialRampToValueAtTime(0.0005, t0 + 0.04);
  nb.connect(bp); bp.connect(ng); ng.connect(space);
  nb.start(t0); nb.stop(t0 + 0.06);
}

/* set-down foley — dead, dry, felt-lined: a miss must never sound rewarded */
function put() {
  if (!ac) return;
  const t0 = ac.currentTime + 0.01;
  const o = ac.createOscillator();
  o.type = 'triangle';
  o.frequency.value = 220;
  const g = ac.createGain();
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(0.045, t0 + 0.003);
  g.gain.exponentialRampToValueAtTime(0.0002, t0 + 0.16);
  o.connect(g); g.connect(master);
  o.start(t0); o.stop(t0 + 0.2);
  const nb = ac.createBufferSource(); nb.buffer = noiseBuf;
  const lp = ac.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 420;
  const ng = ac.createGain();
  ng.gain.setValueAtTime(0.05, t0);
  ng.gain.exponentialRampToValueAtTime(0.0005, t0 + 0.07);
  nb.connect(lp); lp.connect(ng); ng.connect(master);
  nb.start(t0); nb.stop(t0 + 0.09);
}

/* one lub-dub at the completion pulse — the heart comes back to life */
function lubdub() {
  if (!ac) return;
  if (ac.state !== 'running') ac.resume();
  const beat = (at, fA, fB, peak) => {
    const o = ac.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(fA, at);
    o.frequency.exponentialRampToValueAtTime(fB, at + 0.3);
    const g = ac.createGain();
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(peak, at + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0002, at + 0.4);
    o.connect(g); g.connect(master);
    o.start(at); o.stop(at + 0.5);
  };
  const t0 = ac.currentTime + 0.02;
  beat(t0, 54, 38, 0.13);
  beat(t0 + 0.16, 48, 34, 0.09);
}

/* ambient room tone — a sealed vault that audibly 'opens' as the heart heals */
let bed = null;
function startBed() {
  if (!ac || bed) return;
  const g = ac.createGain();
  g.gain.setValueAtTime(0, ac.currentTime);
  g.gain.linearRampToValueAtTime(1, ac.currentTime + 1.618);
  g.connect(master);
  const lp = ac.createBiquadFilter();
  lp.type = 'lowpass'; lp.frequency.value = 380; lp.Q.value = 0.5;
  const o1 = ac.createOscillator(); o1.type = 'sine'; o1.frequency.value = 73.42;   // D2
  const g1 = ac.createGain(); g1.gain.value = 0.028;
  o1.connect(g1); g1.connect(g);
  const o2 = ac.createOscillator(); o2.type = 'sine'; o2.frequency.value = 110; o2.detune.value = 4;  // A2
  const g2 = ac.createGain(); g2.gain.value = 0.016;
  o2.connect(g2); g2.connect(g);
  // a LONG looped noise buffer — looping the 0.1s transient buffer would flutter at 10Hz
  const nbuf = ac.createBuffer(1, ac.sampleRate * 2, ac.sampleRate);
  const nd = nbuf.getChannelData(0);
  for (let i = 0; i < nd.length; i++) nd[i] = Math.random() * 2 - 1;
  const ns = ac.createBufferSource(); ns.buffer = nbuf; ns.loop = true;
  const ng = ac.createGain(); ng.gain.value = 0.012;
  ns.connect(lp); lp.connect(ng); ng.connect(g);
  const lfo = ac.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = 0.08; // the breathing
  const lg = ac.createGain(); lg.gain.value = 0.005;
  lfo.connect(lg); lg.connect(ng.gain);
  [o1, o2, ns, lfo].forEach(n => n.start());
  bed = { g, lp, nodes: [o1, o2, ns, lfo] };
}
function bedOpen(step) {          // each placed fragment opens the air a little
  if (bed && ac) bed.lp.frequency.setTargetAtTime(380 + step * 35, ac.currentTime, 0.8);
}
function stopBed() {
  if (!bed || !ac) return;
  const b = bed; bed = null;
  b.g.gain.cancelScheduledValues(ac.currentTime);
  b.g.gain.setValueAtTime(b.g.gain.value, ac.currentTime);
  b.g.gain.linearRampToValueAtTime(0, ac.currentTime + 1.618);
  const stopAt = ac.currentTime + 1.8;
  b.nodes.forEach(n => { try { n.stop(stopAt); } catch (e) {} });
}

function setMuted(m) {
  muted = m;
  store.set('bh_heart_muted', m ? '1' : '0');
  if (master) master.gain.value = m ? 0 : 1;
  ui.sound.setAttribute('aria-pressed', String(!m));
  ui.sound.setAttribute('aria-label', m ? 'sound off' : 'sound on');
  ui.soundCaption.textContent = m ? 'sound off' : 'sound on';
  ui.soundCaption.classList.add('is-in');
  clearTimeout(setMuted._t);
  setMuted._t = setTimeout(() => ui.soundCaption.classList.remove('is-in'), 1000);
}

/* ═══ 06 STATE & SCREENS ══════════════════════════════════════════════ */

const ui = {
  title: ROOT.querySelector('.bhg-screen--title'),
  sub: ROOT.querySelector('.bhg-sub'),
  begin: ROOT.querySelector('.bhg-btn--begin'),
  eight: ROOT.querySelector('.bhg-eight'),
  hud: ROOT.querySelector('.bhg-hud'),
  lozenges: [...ROOT.querySelectorAll('.bhg-lozenges i')],
  roman: ROOT.querySelector('.bhg-roman'),
  sound: ROOT.querySelector('.bhg-sound'),
  soundCaption: ROOT.querySelector('.bhg-sound__caption'),
  hint: ROOT.querySelector('.bhg-hint'),
  whisper: ROOT.querySelector('.bhg-whisper'),
  done: ROOT.querySelector('.bhg-screen--done'),
  headline: ROOT.querySelector('.bhg-done__headline'),
  support: ROOT.querySelector('.bhg-done__support'),
  time: ROOT.querySelector('.bhg-done__time'),
  best: ROOT.querySelector('.bhg-done__best'),
  signature: ROOT.querySelector('.bhg-signature'),
  rule: ROOT.querySelector('.bhg-rule--done'),
  claim: ROOT.querySelector('.bhg-claim'),
  enter: ROOT.querySelector('.bhg-btn--primary'),
  again: ROOT.querySelector('.bhg-btn--again'),
  tune: ROOT.querySelector('.bhg-tune'),
};
ui.enter.href = ENTER_URL;

let state = 'LOADING';
let stateEnter = performance.now();
let placedCount = 0;
let inputEnabled = false;
let ghostBase = 0;            // resting ghost alpha, tweened by states
let ghostFade = 1;            // multiplier, fades the ghost out at completion
let dimAlpha = 0;             // end-of-game canvas dim
let scarBase = 0;             // 0→1, "seams ignite"
let pulse = { scale: 1, dy: 0 };
let sweepP = -1;              // 0..1 while the light sweep crosses
let gleam = null;             // active scar gleam {seam, t0}
let nextGleam = 0;
let timer = { running: false, started: false, ms: 0 };
let hintShown = false;
let lastProgressAt = 0;       // hesitation-breath timer
let ghostBreath = 0;          // extra ghost alpha from the breath
let sparkle = null;           // vitrine glint on one resting fragment
let nextSparkle = 0;
let nextTitleGlint = 0;
let sweepT0 = 0, sweepGain = 1, nextSweep = 0;
let seamKisses = [];          // light running a shared crack when neighbors reunite

function setState(next) {
  state = next;
  stateEnter = performance.now();
  cues = [];                      // stale cues must never fire on the new state's clock
  if (enterHandlers[next]) enterHandlers[next]();
}
const elapsed = () => performance.now() - stateEnter;

const enterHandlers = {

  /* the breaking rite: the dagger descends, the heart breaks — then you rebuild it */
  PROLOGUE() {
    inputEnabled = false;
    ui.title.classList.add('is-out');
    if (!ui.done.hidden) ui.done.classList.add('is-out');
    const v = breakVideo;
    if (!v || v.error || v.readyState === 0 || reduced()) { setState('INTRO'); return; }
    prologueDone = false;
    try { v.currentTime = 0; } catch (e) {}
    const play = v.play();
    if (play && play.catch) play.catch(() => endPrologue());
    v.classList.add('is-in');
    const d0 = dimAlpha;
    tween(T_STD, t => t, t => { dimAlpha = lerp(d0, 0.85, t); });
    setCues([
      [1900, () => thud()],                        // the blade lands
      [3600, () => endPrologue()],                 // safety net past the 3s runtime
    ]);
  },

  TITLE() {
    ghostBase = 0.05;
    ui.begin.disabled = false;
    ui.begin.classList.add('is-ready');
    ui.eight.classList.add('is-in');
    ui.sub.classList.remove('is-breathing');
    // the house remembers a returning hand
    if (!creditOnReturn() && store.get('bh_heart_best_ms')) ui.sub.textContent = 'it broke again.';
    nextTitleGlint = performance.now() + rand(3000, 6000);
  },

  INTRO() {
    inputEnabled = false;
    placedCount = 0;
    timer = { running: false, started: false, ms: 0 };
    chimeStep = 0;
    hintShown = false;
    ui.lozenges.forEach(l => l.classList.remove('is-filled'));
    ui.roman.classList.remove('is-in');
    ui.roman.textContent = '';
    ui.hint.hidden = false;
    ui.whisper.hidden = true;
    ui.whisper.classList.remove('is-in');
    if (dimAlpha > 0) { const d0 = dimAlpha; tween(T_STD, t => t, t => { dimAlpha = d0 * (1 - t); }); }
    scarBase = 0;
    ghostFade = 1;
    sweepP = -1;
    gleam = null;
    seamKisses = [];
    sparkle = null;
    ghostBreath = 0;
    lastProgressAt = performance.now();
    pulse = { scale: 1, dy: 0 };

    const reshatter = introMode === 'reshatter';
    placedCtx && identity(placedCtx);
    placedCtx && placedCtx.clearRect(0, 0, placedCanvas.width, placedCanvas.height);

    /* shatter continuity: the prologue video ends on the broken heart at
       center — first-run fragments are BORN there and radiate outward to
       their scatter spots, one radial burst. Reshatter flies target→scatter. */
    const dur = reduced() ? 1 : 988;
    const gen = ++introGen;
    PIECES.forEach((p, i) => {
      p.placed = false; p.locking = false;
      const sc = clampToField(p, VP.portrait ? p.portX : p.startX, VP.portrait ? p.portY : p.startY);
      const fromX = reshatter ? p.targetX : lerp(HEART_CENTER.x, sc.x, 0.22);
      const fromY = reshatter ? p.targetY : lerp(HEART_CENTER.y, sc.y, 0.22);
      p.x = fromX; p.y = fromY;
      p.rot = reshatter ? 0 : rand(-14, 14);
      p.alpha = reshatter ? 1 : 0;
      p.dispScale = 1;
      p.floatFactor = 0;
      p.phase = i * 2.39996;                       // golden angle — never in sync
      const delay = i * (reshatter ? 70 : 40);
      setTimeout(() => {
        if (gen !== introGen) return;
        if (state !== 'INTRO' && state !== 'PLAYING') return;
        const x0 = p.x, y0 = p.y, r0 = p.rot;
        tween(dur, easeOutQuint, t => {
          if (p.dragging || p.placed) return;
          p.x = lerp(x0, sc.x, t);
          p.y = lerp(y0, sc.y, t);
          p.rot = lerp(r0, p.rotation, t);
          p.alpha = reshatter ? 1 : clamp01(t / 0.25);   // materialize out of the burst
        }, () => { p.floatFactor = 1; });
      }, delay);
    });

    if (!reshatter && !reduced()) {
      // one soft central flash + a handful of outward motes: the handoff
      fxFlashes.push({ x: HEART_CENTER.x, y: HEART_CENTER.y, size: 520, t0: performance.now(), dur: T_STD, a: 0.22 });
      for (let i = 0; i < 8; i++) {
        const a = rand(0, Math.PI * 2), sp = rand(40, 90);
        spawnParticle({
          x: HEART_CENTER.x, y: HEART_CENTER.y,
          vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, g: 0,
          ttl: rand(900, 1400), size: rand(2, 4),
          sprite: Math.random() < 0.5 ? 0 : 1, a0: rand(0.2, 0.35),
        });
      }
    }

    if (reduced()) {
      setCues([[50, () => { ghostBase = 0.085; ui.hud.hidden = false; ui.hud.classList.add('is-in'); }],
               [120, () => setState('PLAYING')]]);
    } else {
      setCues([
        [0,    () => { ui.title.classList.add('is-out'); ui.done.classList.add('is-out'); }],
        [618,  () => { const g0 = ghostBase; tween(988, t => t, t => { ghostBase = lerp(g0, 0.085, t); }); }],
        [700,  () => { ui.done.hidden = true; ui.done.classList.remove('is-out'); resetDoneReveals(); }],
        [1600, () => { inputEnabled = true; }],
        [1854, () => {
          ui.hud.hidden = false;
          requestAnimationFrame(() => ui.hud.classList.add('is-in'));
          ui.hint.classList.add('is-in');
          hintShown = true;
          setTimeout(() => ui.hint.classList.remove('is-in'), 5000);
        }],
        [2472, () => setState('PLAYING')],
      ]);
    }
    introMode = 'first';
  },

  PLAYING() {
    inputEnabled = true;
    ui.title.hidden = true;
    if (ui.hud.hidden) { ui.hud.hidden = false; ui.hud.classList.add('is-in'); }
    startBed();
    lastProgressAt = performance.now();
    nextSparkle = performance.now() + rand(9000, 14000);
  },

  COMPLETING() {
    inputEnabled = false;
    timer.running = false;
    seamKisses = [];
    sparkle = null;
    const fast = reduced();
    if (fast) {
      scarBase = 1;
      ghostFade = 0;
      dimAlpha = 0.35;
      setCues([
        [80,  () => showDone(true)],
        [400, () => setState('DONE')],
      ]);
      pad();
      stopBed();
      return;
    }
    setCues([
      [0,    () => {
        pad(); lubdub(); buzz([18, 110, 26]);
        tween(T_STD, t => t, t => { pulse.scale = 1 + 0.035 * Math.sin(Math.PI * t); });
      }],
      [618,  () => { tween(T_STD, t => t, t => { scarBase = t; }); }],
      [309,  () => { sweepT0 = performance.now(); sweepGain = 1; sweepP = 0; }],
      [1236, () => { tween(T_STD, easeInOutSine, t => { pulse.dy = 6 * Math.sin(Math.PI * t); }); }],
      [1854, () => { tween(T_CINE, t => t, t => { ghostFade = 1 - t; }); stopBed(); }],
      [3090, () => { tween(T_STD, t => t, t => { dimAlpha = 0.35 * t; }); showDone(false); }],
      [3090, () => reveal(ui.headline)],
      [3399, () => reveal(ui.support)],
      [3550, () => reveal(ui.time)],
      [3700, () => reveal(ui.best)],
      [3850, () => reveal(ui.rule)],
      [3925, () => reveal(ui.claim)],
      [4017, () => { reveal(ui.signature); ui.signature.classList.add('is-in'); reveal(ui.enter); }],
      [4167, () => reveal(ui.again)],
      [4635, () => setState('DONE')],
    ]);
  },

  DONE() {
    nextGleam = performance.now() + rand(2000, 4000);
    nextSweep = performance.now() + rand(30000, 45000);
    ui.hud.classList.remove('is-in');
    setTimeout(() => { ui.hud.hidden = true; }, T_STD);
  },
};

let introMode = 'first';
let introGen = 0;
let breakVideo = null, prologueDone = true;

function setupBreakVideo() {
  if (!BREAK_VIDEO) return;
  breakVideo = ROOT.querySelector('.bhg-break');
  if (!breakVideo) return;
  breakVideo.src = BREAK_VIDEO;
  breakVideo.load();
  breakVideo.addEventListener('ended', () => endPrologue());
  breakVideo.addEventListener('error', () => { breakVideo = null; });
}

function endPrologue() {
  if (prologueDone || state !== 'PROLOGUE') return;
  prologueDone = true;
  if (breakVideo) {
    breakVideo.classList.remove('is-in');
    setTimeout(() => { try { breakVideo.pause(); } catch (e) {} }, T_STD);
  }
  setState('INTRO');
}

function reveal(el) { if (el) el.classList.add('is-in'); }
function doneReveals() {
  return [ui.headline, ui.support, ui.time, ui.best, ui.rule, ui.claim, ui.signature, ui.enter, ui.again].filter(Boolean);
}
function resetDoneReveals() {
  for (const el of doneReveals()) el.classList.remove('is-in');
}

/* Fragmenta claim — fires once per completion screen, dedup on both sides */
function claimFragments() {
  const el = ui.claim;
  if (!el || !CLAIM.url) return;
  if (!CLAIM.customerId) {
    if (CLAIM.login) {
      const back = encodeURIComponent(location.pathname);
      el.innerHTML = `<a href="${CLAIM.login}?return_url=${back}">create your account — ten thousand fragments, set in your name.</a>`;
    }
    return;
  }
  if (store.get('bh_heart_claimed') === CLAIM.customerId) {
    el.textContent = 'your fragments are already set.';
    return;
  }
  el.textContent = '';
  fetch(CLAIM.url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ customer_id: CLAIM.customerId, token: CLAIM.token, game: 'heart-game' }),
  }).then(r => {
    if (r.ok) {
      store.set('bh_heart_claimed', CLAIM.customerId);
      el.textContent = 'ten thousand fragments. set in your name.';
    } else if (r.status === 409) {
      store.set('bh_heart_claimed', CLAIM.customerId);
      el.textContent = 'your fragments are already set.';
    }
    // any other failure: silence — the rite never shows an error
  }).catch(() => {});
}

function showDone(instant) {
  const ms = timer.ms;
  ui.time.textContent = `made whole in ${fmtTime(ms)}`;
  const prev = Number(store.get('bh_heart_best_ms')) || 0;
  if (!prev || ms < prev) {
    store.set('bh_heart_best_ms', String(Math.round(ms)));
    ui.best.textContent = prev ? `fastest restoration — ${fmtTime(ms)}` : '';
  } else {
    // the standing record is part of the house's memory — never an empty line
    ui.best.textContent = `fastest restoration — ${fmtTime(prev)}`;
  }
  ui.done.hidden = false;
  store.set('bh_heart_completed', '1');
  claimFragments();
  if (instant) for (const el of doneReveals()) reveal(el);
}

function creditOnReturn() {
  if (!CLAIM.url || !CLAIM.customerId) return false;
  if (store.get('bh_heart_completed') !== '1') return false;
  if (store.get('bh_heart_claimed') === CLAIM.customerId) {
    if (ui.sub) ui.sub.textContent = 'your fragments are set. the heart waits, if you wish to break it again.';
    return true;
  }
  fetch(CLAIM.url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customer_id: CLAIM.customerId, token: CLAIM.token, game: 'heart-game' }) })
    .then(r => { if (r.ok || r.status === 409) { store.set('bh_heart_claimed', CLAIM.customerId); if (ui.sub) ui.sub.textContent = r.ok ? 'the heart remembers you. ten thousand fragments, set in your name.' : 'your fragments are already set.'; } }).catch(() => {});
  return true;
}

/* ═══ 07 INPUT ════════════════════════════════════════════════════════ */

const drag = { piece: null, pointerId: null, offX: 0, offY: 0, lift: 0, vx: 0, vy: 0, lastX: 0, lastY: 0, lastT: 0, tilt: 0, lagY: 0, inField: false };
const LIFT = COARSE ? -40 : -16;   // on touch the gem rides clear of the thumb

function toDesign(e) {
  const r = canvas.getBoundingClientRect();
  return { x: (e.clientX - r.left - VP.tx) / VP.k, y: (e.clientY - r.top - VP.ty) / VP.k };
}

function pieceVisualCenter(p) {
  const s = p.scale;
  return { x: p.x + (p.bbox.x + p.bbox.w / 2 - 128) * s, y: p.y + (p.bbox.y + p.bbox.h / 2 - 128) * s };
}

function hitTest(dx, dy) {
  const candidates = PIECES.filter(p => TUNE ? true : !p.placed && !p.locking).sort((a, b) => b.z - a.z);
  for (const p of candidates) {
    const ex = p.x + floatDX(p), ey = p.y + floatDY(p);
    const a = -(p.rot + floatRot(p)) * DEG;
    const lx = (dx - ex) * Math.cos(a) - (dy - ey) * Math.sin(a);
    const ly = (dx - ex) * Math.sin(a) + (dy - ey) * Math.cos(a);
    const sx = lx / p.scale / p.dispScale + 128, sy = ly / p.scale / p.dispScale + 128;
    if (sx < 0 || sx >= 256 || sy < 0 || sy >= 256) continue;
    const mi = ((sy / 4) | 0) * 64 + ((sx / 4) | 0);
    if (p.mask[mi * 4 + 3] > 16) return p;
  }
  // fat-finger fallback: nearest unplaced visual center (wider on touch)
  let best = null, bestD = COARSE ? 84 : 60;
  for (const p of candidates) {
    const c = pieceVisualCenter(p);
    const d = dist(dx, dy, c.x, c.y);
    if (d < bestD) { bestD = d; best = p; }
  }
  return best;
}

let zTop = 8;

canvas.addEventListener('pointerdown', e => {
  if (state === 'PROLOGUE' && elapsed() > 400) {             // skip the rite
    endPrologue();
    return;
  }
  if (state === 'INTRO' && elapsed() > 400 && !reduced()) {  // skip the intro
    skipIntro();
    return;
  }
  if (!inputEnabled && !TUNE) return;
  if (drag.piece) return;
  const d = toDesign(e);
  const p = hitTest(d.x, d.y);
  if (!p) return;
  e.preventDefault();
  canvas.setPointerCapture(e.pointerId);
  drag.piece = p;
  drag.pointerId = e.pointerId;
  drag.offX = p.x - d.x;
  drag.offY = p.y - d.y;
  drag.lift = 0;
  drag.vx = 0; drag.vy = 0; drag.lagY = 0;
  drag.lastX = e.clientX; drag.lastY = e.clientY; drag.lastT = performance.now();
  drag.tilt = 0;
  drag.inField = false;
  p.dragging = true;
  p.z = ++zTop;
  if (!TUNE) {                    // tune mode needs raw positions — no lift offset in the target
    tween(120, easeOutCubic, t => { drag.lift = LIFT * t; if (p.dispScale < 1.046) p.dispScale = lerp(1, 1.045, t); });
    tween(150, t => t, t => { p.floatFactor = 1 - t; });
    pick();
    buzz(4);
  }
  if (!timer.started && state === 'PLAYING') { timer.started = true; timer.running = true; }
  if (ac && ac.state !== 'running') ac.resume();
  if (tuneState) tuneState.selected = PIECES.indexOf(p);
});

canvas.addEventListener('pointermove', e => {
  const p = drag.piece;
  if (!p || e.pointerId !== drag.pointerId) return;
  const d = toDesign(e);
  p.x = d.x + drag.offX;
  p.y = d.y + drag.offY + drag.lift;
  const now = performance.now();
  if (now - drag.lastT > 16) {
    const dts = (now - drag.lastT) / 1000;
    drag.vx = (e.clientX - drag.lastX) / dts / VP.k;
    drag.vy = (e.clientY - drag.lastY) / dts / VP.k;
    drag.lastX = e.clientX; drag.lastY = e.clientY; drag.lastT = now;
  }
  if (TUNE) { p.targetX = p.x; p.targetY = p.y; refreshTune(); }
});

function endDrag(e) {
  const p = drag.piece;
  if (!p || (e && e.pointerId !== drag.pointerId)) return;
  const start = renderPos(p);     // capture what the eye sees BEFORE clearing drag state
  drag.piece = null;
  p.dragging = false;
  if (TUNE) { p.dispScale = 1; refreshTune(); return; }
  const d = dist(p.x, p.y, p.targetX, p.targetY);
  if (d < p.snapRadius && state === 'PLAYING') {
    snap(p, start);
  } else {
    const c = clampToField(p, p.x, p.y - drag.lift);
    const x0 = p.x, y0 = p.y, s0 = p.dispScale;
    p.phase = rand(0, Math.PI * 2);
    put();
    tween(180, easeOutCubic, t => {
      if (p.dragging || p.placed) return;
      p.x = lerp(x0, c.x, t); p.y = lerp(y0, c.y, t); p.dispScale = lerp(s0, 1, t);
    }, () => { p.floatFactor = 1; });
    tween(T_STD, t => t, t => { if (!p.dragging) p.rot = lerp(p.rot, p.rotation, t); });
  }
}
canvas.addEventListener('pointerup', endDrag);
canvas.addEventListener('pointercancel', endDrag);

function skipIntro() {
  introGen++;                     // orphan any pending per-piece intro timeouts
  clearTweens();
  dimAlpha = 0;                   // the prologue dim fade was in those tweens
  lastProgressAt = performance.now();
  PIECES.forEach(p => {
    if (p.placed) return;
    const sc = clampToField(p, VP.portrait ? p.portX : p.startX, VP.portrait ? p.portY : p.startY);
    p.x = sc.x; p.y = sc.y; p.rot = p.rotation; p.alpha = 1; p.floatFactor = 1;
  });
  ghostBase = 0.085;
  ui.title.classList.add('is-out');
  ui.done.hidden = true;
  resetDoneReveals();
  ui.hud.hidden = false;
  ui.hud.classList.add('is-in');
  setState('PLAYING');
}

/* ═══ 08 GAME — magnet, snap, progress ════════════════════════════════ */

let chimeStep = 0;

function magnetState(p) {
  const d = dist(p.x, p.y, p.targetX, p.targetY);
  const t = clamp01(1 - d / MAGNET_R);
  return { d, t, pull: t * t * 0.45 };
}

/* render position: the finger keeps authority; the gem yearns */
function renderPos(p) {
  if (!p.dragging || TUNE) return { x: p.x, y: p.y, rot: p.rot };
  const m = magnetState(p);
  if (m.t > 0 && !drag.inField) { drag.inField = true; buzz(6); }
  if (m.t === 0) drag.inField = false;
  const tiltTarget = clamp(drag.vx * 0.012, -2.5, 2.5);
  drag.tilt = lerp(drag.tilt, tiltTarget, 0.15);
  // vertical pendulum lag — mass without input latency
  if (!reduced()) drag.lagY = lerp(drag.lagY, clamp(-drag.vy * 0.008, -5, 5), 0.12);
  return {
    x: lerp(p.x, p.targetX, m.pull),
    y: lerp(p.y, p.targetY, m.pull) + drag.lagY,
    rot: lerp(p.rot + drag.tilt, p.targetRotation, m.t * m.t),
  };
}

function snap(p, start) {
  p.locking = true;
  start = start || { x: p.x, y: p.y, rot: p.rot };
  const s0 = p.dispScale;
  const isLast = placedCount === 7;
  const quick = reduced();
  if (quick) {
    p.x = p.targetX; p.y = p.targetY; p.rot = p.targetRotation; p.dispScale = 1;
    chime(chimeStep++);
    finishSnap(p);
    return;
  }
  tween(110, easeOutCubic, t => {
    p.x = lerp(start.x, p.targetX, t);
    p.y = lerp(start.y, p.targetY, t);
    p.rot = lerp(start.rot, p.targetRotation, t);
  });
  tween(70, t => t, t => { p.dispScale = lerp(s0, 1.07, t); }, () => {
    impactFx(p, isLast);
    tween(110, easeOutBack, t => { p.dispScale = lerp(1.07, 1, t); });
    setTimeout(() => finishSnap(p), 90);           // stamp at ~160ms after release
  });
}

function impactFx(p, isLast) {
  chime(chimeStep++);
  if (isLast) thud(0.1, 64, 40);                   // the eighth lands with body
  buzz(isLast ? [14, 50, 10] : 12);
  const span = Math.max(p.bbox.w, p.bbox.h) * p.scale;
  if (isLast) {
    fxFlashes.push({ x: p.targetX, y: p.targetY, size: span * 1.8, t0: performance.now(), dur: 240, a: 0.4 });
    fxFlashes.push({ x: p.targetX, y: p.targetY, size: span * 2.6, t0: performance.now(), dur: 520, a: 0.22 });
  } else {
    fxFlashes.push({ x: p.targetX, y: p.targetY, size: span * 1.5, t0: performance.now(), dur: 240, a: 0.32 });
    fxFlashes.push({ x: p.targetX, y: p.targetY, size: span * 2.1, t0: performance.now(), dur: 430, a: 0.18 });
  }
  const n = isLast ? (16 + Math.random() * 4) | 0 : (10 + Math.random() * 6) | 0;
  for (let i = 0; i < n; i++) {
    const a = rand(0, Math.PI * 2), sp = rand(60, 160);
    spawnParticle({
      x: p.targetX + Math.cos(a) * span * 0.3,
      y: p.targetY + Math.sin(a) * span * 0.3,
      vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 10,
      g: -20, ttl: rand(600, 900), size: rand(2, 4.5),
      sprite: Math.random() < 0.12 ? 2 : (Math.random() < 0.5 ? 0 : 1),
      a0: rand(0.3, 0.6),
    });
  }
}

function finishSnap(p) {
  if (p.placed) return;
  p.placed = true;
  p.locking = false;
  stampPiece(p);
  placedCount++;
  lastProgressAt = performance.now();
  bedOpen(placedCount);
  ui.lozenges[placedCount - 1].classList.add('is-filled');
  ui.roman.innerHTML = `<b>${ROMAN[placedCount - 1]}</b> of <b>VIII</b> restored`;
  ui.roman.classList.add('is-in');
  if (hintShown) ui.hint.classList.remove('is-in');

  if (!reduced() && placedCount < 8) {
    // acceptance: the placed cluster breathes once, a little deeper each time
    const amp = Math.min(0.012, 0.005 + 0.0007 * placedCount);
    tween(T_PULL, t => t, t => { pulse.scale = 1 + amp * Math.sin(Math.PI * t); });
    // seam-kiss: light runs each crack this piece now shares with a placed neighbor
    let ki = 0;
    for (const s of SEAMS_REF) {
      if (!s.pair.includes(p.id)) continue;
      const other = PIECES.find(q => q.id === (s.pair[0] === p.id ? s.pair[1] : s.pair[0]));
      if (other && other.placed && ki < 3) {
        seamKisses.push({ seam: seamDesign(s.pts), t0: performance.now() + 150 * ki++ });
      }
    }
  }

  if (placedCount === 7) {
    ui.whisper.hidden = false;
    requestAnimationFrame(() => ui.whisper.classList.add('is-in'));
    setTimeout(() => ui.whisper.classList.remove('is-in'), 2400);
    setTimeout(() => { ui.whisper.hidden = true; }, 3100);
  }
  if (placedCount === 8) {
    timer.running = false;
    // a held breath before the heart answers
    setTimeout(() => setState('COMPLETING'), reduced() ? 250 : T_STD);
  }
}

/* ═══ 09 FX — particles, flashes, grain, scar light, sweep ════════════ */

const POOL = 120;
const particles = Array.from({ length: POOL }, () => ({ live: false }));
const fxFlashes = [];

function spawnParticle(o) {
  for (const pt of particles) {
    if (pt.live) continue;
    Object.assign(pt, o, { live: true, t0: performance.now(), sway: rand(0, Math.PI * 2) });
    return;
  }
}

function updateParticles(dt) {
  const now = performance.now();
  for (const pt of particles) {
    if (!pt.live) continue;
    const age = now - pt.t0;
    if (age > pt.ttl) { pt.live = false; continue; }
    pt.vy += (pt.g || 0) * dt;
    pt.x += pt.vx * dt;
    pt.y += pt.vy * dt;
    if (pt.swayAmp) pt.x += Math.sin(now / 1000 * (Math.PI * 2 / pt.swayPeriod) + pt.sway) * pt.swayAmp * dt;
  }
}

function drawParticles() {
  ctx.globalCompositeOperation = 'lighter';
  const now = performance.now();
  for (const pt of particles) {
    if (!pt.live) continue;
    const k = (now - pt.t0) / pt.ttl;
    const fade = k > 0.66 ? 1 - (k - 0.66) / 0.34 : 1;
    ctx.globalAlpha = pt.a0 * fade;
    const spr = caches.dots[pt.sprite];
    const sz = pt.size * (pt.sprite === 2 ? 2.2 : 1);
    ctx.drawImage(spr, pt.x - sz, pt.y - sz, sz * 2, sz * 2);
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
}

function drawFlashes() {
  if (!fxFlashes.length) return;
  ctx.globalCompositeOperation = 'lighter';
  const now = performance.now();
  for (let i = fxFlashes.length - 1; i >= 0; i--) {
    const f = fxFlashes[i];
    const k = (now - f.t0) / f.dur;
    if (k >= 1) { fxFlashes.splice(i, 1); continue; }
    ctx.globalAlpha = f.a * (1 - k);
    ctx.drawImage(caches.flash, f.x - f.size / 2, f.y - f.size / 2, f.size, f.size);
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
}

let grainIdx = 0, grainSwap = 0, grainOff = { x: 0, y: 0 };
function drawGrain(now) {
  if (!caches.grain.length) return;
  if (!reduced() && now - grainSwap > 80) {        // 12fps grain reads as film, 60fps as video noise
    grainSwap = now;
    grainIdx = (grainIdx + 1) % 3;
    grainOff = { x: (Math.random() * 160) | 0, y: (Math.random() * 160) | 0 };
  }
  identity(ctx);
  ctx.globalAlpha = 0.04;
  ctx.globalCompositeOperation = 'overlay';
  ctx.save();
  ctx.translate(-grainOff.x, -grainOff.y);
  ctx.fillStyle = caches.grainPat[grainIdx];
  ctx.fillRect(0, 0, canvas.width + 160, canvas.height + 160);
  ctx.restore();
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
}

const seamDesign = seam => seam.map(([x, y]) => ({ x: REF_OFFSET.x + x * REF_K, y: REF_OFFSET.y + y * REF_K }));

function drawScarLight(now) {
  if (scarBase <= 0.005 && !seamKisses.length) return;
  const hr = heartRect();
  const px = VP.k * VP.dpr;
  const sc = caches.scar;
  const x = sc.getContext('2d');
  identity(x);
  x.clearRect(0, 0, sc.width, sc.height);
  if (scarBase > 0.005) {
    x.globalAlpha = 0.32 * scarBase;
    x.drawImage(caches.scarRest, 0, 0);
  }
  const spotR = caches.scarSpot.width / 2;
  const drawSpot = (seam, k, peak, radiusScale) => {
    const t = easeInOutSine(k);
    const [a, b] = seam;
    const gx = (lerp(a.x, b.x, t) - hr.x) * px;
    const gy = (lerp(a.y, b.y, t) - hr.y) * px;
    const r = spotR * radiusScale;
    x.globalAlpha = peak * Math.sin(Math.PI * k);
    x.drawImage(caches.scarSpot, gx - r, gy - r, r * 2, r * 2);
  };
  // seam-kiss: light runs the crack two reunited neighbors now share
  for (let i = seamKisses.length - 1; i >= 0; i--) {
    const kiss = seamKisses[i];
    const k = (now - kiss.t0) / T_STD;
    if (k >= 1) { seamKisses.splice(i, 1); continue; }
    if (k > 0) drawSpot(kiss.seam, k, 0.35, 0.55);
  }
  // travelling gleam: a hotspot runs one seam, then darkness again
  if (state === 'DONE' || state === 'COMPLETING') {
    if (!gleam && now >= nextGleam && state === 'DONE' && !reduced()) {
      gleam = { seam: seamDesign(SEAMS_REF[(Math.random() * SEAMS_REF.length) | 0].pts), t0: now };
      nextGleam = now + rand(7000, 11000);
    }
    if (gleam) {
      const k = (now - gleam.t0) / T_CINE;
      if (k >= 1) { gleam = null; }
      else drawSpot(gleam.seam, k, 0.45, 1);
    }
  }
  x.globalAlpha = 1;
  x.globalCompositeOperation = 'destination-in';
  x.drawImage(caches.hullMask, 0, 0);
  x.globalCompositeOperation = 'source-over';
  designTransform(ctx);
  ctx.drawImage(sc, hr.x, hr.y, hr.w, hr.h);
}

function drawSweep() {
  if (sweepP < 0) return;
  sweepP = clamp01((performance.now() - sweepT0) / 1236);
  const hr = heartRect();
  const sw = caches.sweep;
  const x = sw.getContext('2d');
  identity(x);
  x.clearRect(0, 0, sw.width, sw.height);
  const band = caches.sweepBand;
  const t = easeInOutSine(sweepP);
  const ox = lerp(-band.width, sw.width, t);
  x.drawImage(band, ox, -band.height * 0.1);
  x.globalCompositeOperation = 'destination-in';
  x.drawImage(caches.heartMask, 0, 0);
  x.globalCompositeOperation = 'source-over';
  designTransform(ctx);
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = 0.7 * sweepGain * Math.sin(Math.PI * sweepP);
  ctx.drawImage(sw, hr.x, hr.y, hr.w, hr.h);
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  if (sweepP >= 1) sweepP = -1;
}

/* ═══ 10 RENDER ═══════════════════════════════════════════════════════ */

const floatDX  = p => p.floatFactor ? 6.0 * Math.sin(tSec * 0.45 + p.phase) * p.floatFactor * (reduced() ? 0 : 1) : 0;
const floatDY  = p => p.floatFactor ? 4.8 * Math.sin(tSec * 0.62 + p.phase * 1.7) * p.floatFactor * (reduced() ? 0 : 1) : 0;
const floatRot = p => p.floatFactor ? 1.2 * Math.sin(tSec * 0.30 + p.phase * 0.6) * p.floatFactor * (reduced() ? 0 : 1) : 0;

let tSec = 0;

function drawPiece(p) {
  const r = renderPos(p);
  const cache = p.dragging ? p.cacheLift : p.cache;
  ctx.save();
  ctx.translate(r.x + floatDX(p), r.y + floatDY(p));
  ctx.rotate((r.rot + floatRot(p)) * DEG);
  ctx.globalAlpha = p.alpha;
  const sz = cache.size * p.dispScale;
  ctx.drawImage(cache.canvas, -sz / 2, -sz / 2, sz, sz);
  ctx.restore();
}

function ghostAlpha() {
  let a = ghostBase;
  if (!reduced() && (state === 'PLAYING' || state === 'TITLE')) a = ghostBase + 0.015 * Math.sin(tSec * Math.PI * 2 / 7);
  a += ghostBreath;                                                     // the hesitation breath
  if (drag.piece) a += 0.04 * Math.pow(magnetState(drag.piece).t, 2);   // global lift stays subtle —
  if (TUNE && tuneState.overlay === 3) a = 0.5;                         // the slot ghost does the recognizing
  return clamp01(a) * ghostFade;
}

function render(now) {
  identity(ctx);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(caches.bg, 0, 0);

  if (refImg) {
    const hr = heartRect();
    // ghost guide
    designTransform(ctx);
    ctx.globalAlpha = ghostAlpha();
    ctx.drawImage(caches.ghost, hr.x, hr.y, hr.w, hr.h);
    ctx.globalAlpha = 1;

    // tune reference overlay
    if (TUNE && tuneState.overlay >= 2) {
      ctx.globalAlpha = 0.35;
      ctx.drawImage(refImg, hr.x, hr.y, hr.w, hr.h);
      ctx.globalAlpha = 1;
    }

    // slot recognition: the dragged gem's own silhouette glows at its home
    if (drag.piece && !drag.piece.placed && drag.piece.ghostSlot && !TUNE) {
      const m = magnetState(drag.piece);
      if (m.t > 0) {
        const gs = drag.piece.ghostSlot;
        ctx.globalAlpha = 0.16 * m.t * m.t * ghostFade;
        ctx.drawImage(gs.canvas, drag.piece.targetX - gs.size / 2, drag.piece.targetY - gs.size / 2, gs.size, gs.size);
        ctx.globalAlpha = 1;
      }
    }

    // scar light — only visible through the fissures
    drawScarLight(now);

    // locked pieces, one blit (with the completion pulse when active)
    identity(ctx);
    const cxD = (HEART_CENTER.x * VP.k + VP.tx) * VP.dpr;
    const cyD = (HEART_CENTER.y * VP.k + VP.ty) * VP.dpr;
    ctx.save();
    ctx.translate(cxD, cyD + pulse.dy * VP.k * VP.dpr);
    ctx.scale(pulse.scale, pulse.scale);
    ctx.translate(-cxD, -cyD);
    ctx.drawImage(placedCanvas, 0, 0);
    ctx.restore();

    // active pieces, back-to-front; dragged piece always last
    designTransform(ctx);
    const active = PIECES.filter(p => !p.placed).sort((a, b) => a.z - b.z);
    for (const p of active) if (!p.dragging) drawPiece(p);
    if (drag.piece && !drag.piece.placed) drawPiece(drag.piece);

    // vitrine sparkle: one resting stone catches the light
    if (sparkle) {
      const k = (now - sparkle.t0) / 900;
      const sp = sparkle.p;
      if (k >= 1 || sp.dragging || sp.placed || sp.locking) sparkle = null;
      else {
        ctx.save();
        ctx.translate(sp.x + floatDX(sp), sp.y + floatDY(sp));
        ctx.rotate((sp.rot + floatRot(sp)) * DEG);
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = 0.45 * Math.sin(Math.PI * k) * sp.alpha;
        const gx = (sp.bbox.x + sp.bbox.w / 2 - 128) * sp.scale;
        const gy = (sp.bbox.y + sp.bbox.h * 0.30 - 128) * sp.scale;
        ctx.drawImage(caches.dots[2], gx - 9, gy - 9, 18, 18);
        ctx.restore();
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
      }
    }

    drawFlashes();
    drawParticles();
    drawSweep();
    if (TUNE) drawTune();
  }

  // end-of-game dim: flat under the prologue video, pooled at the edges
  // (jewel spotlight) once the heart is whole
  if (dimAlpha > 0) {
    identity(ctx);
    if (state === 'COMPLETING' || state === 'DONE') {
      ctx.globalAlpha = dimAlpha;
      ctx.drawImage(caches.dim, 0, 0);
      ctx.globalAlpha = 1;
    } else {
      ctx.fillStyle = `rgba(3,3,4,${dimAlpha})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }

  drawGrain(now);
}

let rafId = 0, lastT = 0;
function tick(now) {
  rafId = requestAnimationFrame(tick);
  const dt = Math.min(32, now - (lastT || now)) / 1000;
  lastT = now;
  tSec = now / 1000;
  updateTweens(now);
  runCues(elapsed());
  if (timer.running) timer.ms += dt * 1000;
  if (!reduced()) updateParticles(dt);
  // completion rising motes
  if (state === 'COMPLETING' && !reduced()) {
    const e = elapsed();
    if (e > 618 && e < 3090) {
      const hr = heartRect();
      for (let i = 0; i < 2; i++) if (Math.random() < 0.5) spawnParticle({
        x: rand(hr.x + 60, hr.x + hr.w - 60), y: rand(hr.y + 80, hr.y + hr.h - 60),
        vx: rand(-10, 10), vy: rand(-80, -30), g: 0,
        ttl: rand(1800, 3000), size: rand(1.5, 3.5),
        sprite: Math.random() < 0.12 ? 2 : (Math.random() < 0.5 ? 0 : 1),
        a0: rand(0.15, 0.5), swayAmp: rand(3, 8), swayPeriod: rand(2, 3),
      });
    }
  }
  // title vitrine: dust in the case light + a rare glint on the ghost's seams
  if (state === 'TITLE' && !reduced() && refImg) {
    if (Math.random() < dt / 1.8) spawnParticle({
      x: rand(VP.visL + 40, VP.visR - 40), y: rand(450, VP.visB),
      vx: 0, vy: rand(-10, -4), g: 0,
      ttl: rand(9000, 14000), size: rand(1.5, 2.5),
      sprite: Math.random() < 0.5 ? 0 : 1, a0: rand(0.04, 0.10),
      swayAmp: rand(2, 5), swayPeriod: rand(3, 5),
    });
    if (now >= nextTitleGlint) {
      nextTitleGlint = now + rand(6000, 10000);
      const s = SEAMS_REF[(Math.random() * SEAMS_REF.length) | 0];
      const pt = s.pts[(Math.random() * 2) | 0];
      spawnParticle({
        x: REF_OFFSET.x + pt[0] * REF_K, y: REF_OFFSET.y + pt[1] * REF_K,
        vx: 0, vy: 0, g: 0, ttl: 1600, size: rand(2, 3), sprite: 2, a0: 0.22,
      });
    }
  }
  // hesitation breath: the heart calls, once, when the player stalls
  if (state === 'PLAYING' && !drag.piece && !reduced() && now - lastProgressAt > 18000) {
    lastProgressAt = now;
    tween(3236, easeInOutSine, t => { ghostBreath = 0.06 * Math.sin(Math.PI * t); });
  }
  // vitrine sparkle scheduler: one resting stone, long intervals, never a sound
  if (state === 'PLAYING' && !reduced() && now >= nextSparkle) {
    const free = PIECES.filter(p => !p.placed && !p.locking && !p.dragging && p.alpha > 0.9);
    if (free.length) sparkle = { p: free[(Math.random() * free.length) | 0], t0: now };
    nextSparkle = now + rand(9000, 14000);
  }
  // the eternal vitrine pass: a half-strength sweep recrosses the jewel
  if (state === 'DONE' && !reduced() && sweepP < 0 && now >= nextSweep) {
    if (gleam) {
      nextSweep = now + 5000;                       // never stack the two light events
    } else {
      sweepT0 = now; sweepGain = 0.4; sweepP = 0;
      nextSweep = now + rand(30000, 45000);
    }
  }
  render(now);
}

document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    cancelAnimationFrame(rafId);
    lastT = 0;
    if (ac) ac.suspend();
  } else {
    if (ac && !muted) ac.resume();
    cancelAnimationFrame(rafId);              // never let two loops stack
    rafId = requestAnimationFrame(tick);
  }
});

/* ═══ 11 TUNE MODE (?tune=1) ══════════════════════════════════════════ */

const tuneState = TUNE ? { selected: 0, overlay: 2 } : null;

function drawTune() {
  designTransform(ctx);
  if (tuneState.overlay >= 1) {
    for (let i = 0; i < PIECES.length; i++) {
      const p = PIECES[i];
      const sel = i === tuneState.selected;
      ctx.strokeStyle = sel ? 'rgba(244,246,249,0.9)' : 'rgba(244,246,249,0.35)';
      ctx.lineWidth = 1 / VP.k;
      ctx.beginPath();
      ctx.moveTo(p.targetX - 14, p.targetY); ctx.lineTo(p.targetX + 14, p.targetY);
      ctx.moveTo(p.targetX, p.targetY - 14); ctx.lineTo(p.targetX, p.targetY + 14);
      ctx.stroke();
      if (sel) {
        const s = p.scale;
        ctx.strokeRect(p.x + (p.bbox.x - 128) * s, p.y + (p.bbox.y - 128) * s, p.bbox.w * s, p.bbox.h * s);
      }
    }
  }
}

function refreshTune() {
  const p = PIECES[tuneState.selected];
  ui.tune.hidden = false;
  ui.tune.textContent =
    `TUNE  [1-8] select  [arrows] nudge (shift=10)  [ ] rotate  +/- scale\n` +
    `[T] overlay:${tuneState.overlay}  [0] reset piece  [E] export PIECES\n` +
    `${p.id}  target ${p.targetX.toFixed(1)}, ${p.targetY.toFixed(1)}  rot ${p.targetRotation.toFixed(1)}  scale ${p.scale.toFixed(3)}`;
}

function exportPieces() {
  const fmt = n => Number(n.toFixed(1));
  const lines = PIECES.map(p =>
    `  { id:'${p.id}', file:'${p.file}', startX:${fmt(p.startX)}, startY:${fmt(p.startY)}, portX:${fmt(p.portX)}, portY:${fmt(p.portY)},\n` +
    `    targetX:${fmt(p.targetX)}, targetY:${fmt(p.targetY)}, rotation:${p.rotation}, targetRotation:${Number(p.targetRotation.toFixed(1))}, scale:${Number(p.scale.toFixed(3))}, snapRadius:${p.snapRadius},\n` +
    `    bbox:{x:${p.bbox.x},y:${p.bbox.y},w:${p.bbox.w},h:${p.bbox.h}} },`
  );
  const out = `const PIECES = [\n${lines.join('\n')}\n];`;
  console.log(out);
  try { navigator.clipboard.writeText(out); } catch (e) {}
  ui.tune.textContent = 'PIECES exported to clipboard + console.';
  setTimeout(refreshTune, 1200);
}

if (TUNE) {
  window.__BH_TUNE = { pieces: PIECES, state: tuneState };
  window.addEventListener('keydown', e => {
    const p = PIECES[tuneState.selected];
    const step = e.shiftKey ? 10 : 1;
    let used = true;
    switch (e.key) {
      case '1': case '2': case '3': case '4': case '5': case '6': case '7': case '8':
        tuneState.selected = Number(e.key) - 1; break;
      case 'ArrowLeft':  p.targetX -= step; break;
      case 'ArrowRight': p.targetX += step; break;
      case 'ArrowUp':    p.targetY -= step; break;
      case 'ArrowDown':  p.targetY += step; break;
      case '[': p.targetRotation -= e.shiftKey ? 5 : 0.5; break;
      case ']': p.targetRotation += e.shiftKey ? 5 : 0.5; break;
      case '+': case '=': p.scale += 0.004; rebuildPiece(p); break;
      case '-': p.scale -= 0.004; rebuildPiece(p); break;
      case 't': case 'T': tuneState.overlay = (tuneState.overlay + 1) % 4; break;
      case '0': {
        const seed = PIECE_SEED[tuneState.selected];
        Object.assign(p, { targetX: seed.targetX, targetY: seed.targetY, targetRotation: seed.targetRotation, scale: seed.scale });
        rebuildPiece(p);
        break;
      }
      case 'e': case 'E': exportPieces(); return;
      default: used = false;
    }
    if (used) {
      e.preventDefault();
      if (!p.dragging) { p.x = p.targetX; p.y = p.targetY; p.rot = p.targetRotation; }
      refreshTune();
    }
  });
}
function rebuildPiece(p) {
  p.cache = buildPieceCache(p, false);
  p.cacheLift = buildPieceCache(p, true);
}

/* ═══ 12 BOOT ═════════════════════════════════════════════════════════ */

PIECES.forEach((p, i) => {
  p.x = -9999; p.y = -9999; p.rot = 0; p.alpha = 0; p.dispScale = 1;
  p.placed = false; p.locking = false; p.dragging = false;
  p.floatFactor = 0; p.phase = i * 2.39996; p.z = i;
});

ui.sound.addEventListener('click', () => { initAudio(); setMuted(!muted); });
ui.sound.setAttribute('aria-pressed', String(!muted));
if (muted) { ui.sound.setAttribute('aria-label', 'sound off'); }

ui.begin.addEventListener('click', () => {
  initAudio();
  introMode = 'first';
  setState('PROLOGUE');
});

ui.again.addEventListener('click', () => {
  introMode = 'reshatter';
  setState('PROLOGUE');
});

let resizeT = 0;
function onResize() {
  clearTimeout(resizeT);
  resizeT = setTimeout(() => {
    measureViewport();
    rebuildCaches();
    for (const p of PIECES) {
      if (p.placed || p.dragging) continue;
      if (state === 'PLAYING' || state === 'INTRO') {
        const c = clampToField(p, p.x, p.y);
        const x0 = p.x, y0 = p.y;
        if (c.x !== x0 || c.y !== y0) tween(400, easeRitual, t => {
          if (p.dragging || p.placed) return;
          p.x = lerp(x0, c.x, t); p.y = lerp(y0, c.y, t);
        });
      }
    }
  }, 150);
}
window.addEventListener('resize', onResize);
window.addEventListener('orientationchange', onResize);

async function boot() {
  measureViewport();
  buildBg();
  buildGrain();
  buildFxSprites();
  setupBreakVideo();
  rafId = requestAnimationFrame(tick);

  const fontWait = document.fonts && document.fonts.ready
    ? Promise.race([document.fonts.ready, new Promise(r => setTimeout(r, 1500))])
    : Promise.resolve();

  try {
    await Promise.all([loadAssets(), fontWait]);
  } catch (err) {
    ui.sub.textContent = 'the fragments could not be found.';
    ui.sub.classList.remove('is-breathing');
    console.error(err);
    return;
  }

  rebuildCaches();

  if (TUNE) {
    // tune mode: straight to the board, pieces resting at their targets
    ui.title.hidden = true;
    ui.hud.hidden = false;
    ui.hud.classList.add('is-in');
    ghostBase = 0.085;
    inputEnabled = true;
    PIECES.forEach(p => { p.x = p.targetX; p.y = p.targetY; p.rot = p.targetRotation; p.alpha = 1; p.floatFactor = 0; });
    setState('PLAYING');
    refreshTune();
    return;
  }

  setState('TITLE');
}

boot();

})();
